import { Box, Button, type SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { SidesheetRoutes, useSidesheet } from 'app/shared-components/sidesheets/SidesheetProvider';
import { useAppDispatch, useAppSelector } from 'app/store';
import { applyCardsColumnFilters } from 'app/store/filtering/cardsFiltersThunks';
import { selectSearchAndFilter } from 'app/store/filtering/searchAndFilterSlice';
import {
  applyTransactionColumnFilters,
  clearTransactionColumnFilters,
} from 'app/store/filtering/transactionFiltersThunks';
import {
  applyUsersColumnFilters,
  clearUsersColumnFilters,
  retrieveUsersManagementFilterOptions,
} from 'app/store/filtering/userFiltersThunks';
import {
  applyCardGroupsColumnFilters,
  clearCardGroupsColumnFilters,
} from 'app/store/filtering/cardGroupFiltersThunks';
import {
  applyProjectGroupsColumnFilters,
  clearProjectGroupsColumnFilters,
} from 'app/store/filtering/projectGroupFiltersThunks';
import {
  fetchTransactionFilterOptions,
  selectTransactionFilterOptions,
} from 'app/pages/store/transactionFilterOptionsSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import useSidesheetById from 'app/hooks/useSidesheetById';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnFilter, ColumnFilterMap } from 'util/filtering/models';
import { clearFiltersFromCurrentUrl } from 'util/filtering/advancedFilterQueryParams';
import { clearColumnFilterValues } from 'util/filtering/functions';
import { getPageName } from 'util/helpers';
import { CriteriaColumnItem, type FilterChangeProps } from './CriteriaColumnItem';

// SEE-974: advanced-filter coding sections that belong to a specific transaction
// type. When a type is active (action-bar quick filter or the advanced filter's
// own Transaction type pick), only that type's sections show; vendor, status,
// location/department/etc. are common and always shown.
const CODING_FILTER_KEY_TYPE: Record<string, 'gl' | 'project' | 'equipment'> = {
  agaveGlCodeId: 'gl',
  agaveProjectId: 'project',
  agaveSubJobId: 'project',
  agaveCostCodeId: 'project',
  agaveCostTypeId: 'project',
  agaveEquipmentId: 'equipment',
  agaveEmCostCodeId: 'equipment',
  agaveEmCostTypeId: 'equipment',
};

export type TransactionAdvancedSearchColumn = {
  name: string;
  type: 'text' | 'number' | 'date';
};

export type TransactionAdvancedSearchSidesheetParams = {
  pageName: string;
};

function TransactionAdvancedSearchSidesheet() {
  const dispatch = useAppDispatch();
  const sidesheet = useSidesheetById(SidesheetRoutes.TransactionsAdvancedSearch);
  const sidesheetParams = sidesheet?.params as TransactionAdvancedSearchSidesheetParams | undefined;
  const pageName = sidesheetParams?.pageName || getPageName(window.location.pathname);
  const tableFilters = useAppSelector(selectSearchAndFilter)[pageName];
  const { selectedPaidolId } = useAppSelector(selectUserCompanies);
  const { data: filterOptions, loading: filterOptionsLoading } = useAppSelector(
    selectTransactionFilterOptions
  );
  const [localColumnFilters, setLocalColumnFilters] = useState<ColumnFilterMap>(
    tableFilters?.columnFilters || {}
  );
  const { closeSidesheet } = useSidesheet();
  const theme = useTheme();
  const { t } = useTranslation();
  const lastFetchedPaidolIdRef = useRef<string | null>(null);

  // Fetch filter options when sidesheet opens, but only if we don't already have data for this paidolId
  useEffect(() => {
    if (selectedPaidolId) {
      // For transaction pages, fetch transaction filter options
      if (
        pageName.startsWith('overview-transactions') ||
        pageName.startsWith('overview-my-transactions') ||
        pageName.startsWith('profile-my-transactions')
      ) {
        // Only fetch if not already loading and we haven't yet fetched for this paidolId.
        // We rely solely on the ref rather than checking filterOptions because the query
        // can legitimately return null (e.g. company with no transactions). Checking
        // !filterOptions here would cause an infinite loop: fetch → null result →
        // !null is truthy → fetch again → …
        const shouldFetch = !filterOptionsLoading && lastFetchedPaidolIdRef.current !== selectedPaidolId;

        if (shouldFetch) {
          lastFetchedPaidolIdRef.current = selectedPaidolId;
          dispatch(fetchTransactionFilterOptions({ paidolId: selectedPaidolId }));
        }
      } else if (pageName === 'team-members') {
        // For users page, fetch user filter options if column filters are not initialized or company changed
        const shouldFetch =
          !tableFilters?.columnFilters ||
          Object.keys(tableFilters.columnFilters).length === 0 ||
          lastFetchedPaidolIdRef.current !== selectedPaidolId;

        if (shouldFetch) {
          lastFetchedPaidolIdRef.current = selectedPaidolId;
          dispatch(retrieveUsersManagementFilterOptions({ pageName, paidolId: selectedPaidolId }));
        }
      }
    }
  }, [
    selectedPaidolId,
    dispatch,
    filterOptions,
    filterOptionsLoading,
    pageName,
    tableFilters?.columnFilters,
  ]);

  useEffect(() => {
    if (tableFilters && tableFilters.columnFilters && Object.keys(tableFilters.columnFilters).length > 0) {
      setLocalColumnFilters(tableFilters.columnFilters);
    }
  }, [tableFilters?.columnFilters]);

  useEffect(() => {
    const filtersToClear = structuredClone(localColumnFilters);
    if (!filtersToClear) return;

    Object.keys(filtersToClear).forEach((key) => {
      if (
        filtersToClear[key].selectedValues?.length === 0 &&
        filtersToClear[key].minNumber === undefined &&
        filtersToClear[key].maxNumber === undefined
      ) {
        filtersToClear[key].active = false;
      }
    });

    setLocalColumnFilters(filtersToClear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCriteriaRuleChange = (event: SelectChangeEvent, key: string) => {
    setLocalColumnFilters((previousValue) => {
      const changedFilterTable = { ...previousValue[key] };
      changedFilterTable.rule = event.target.value;
      return {
        ...previousValue,
        [key]: changedFilterTable,
      };
    });
  };

  const handleSelectedValuesChange = ({
    key,
    selectedOptions,
    minNumber,
    maxNumber,
    active,
  }: FilterChangeProps) => {
    setLocalColumnFilters((previousValue) => {
      const changedFilterTable = { ...previousValue[key] };
      if (selectedOptions) {
        changedFilterTable.selectedValues = selectedOptions;
      }
      changedFilterTable.minNumber = minNumber;
      changedFilterTable.maxNumber = maxNumber;
      changedFilterTable.active = active;
      return {
        ...previousValue,
        [key]: changedFilterTable,
      };
    });
  };

  const handleApplyColumnFilters = () => {
    if (
      pageName.startsWith('overview-transactions') ||
      pageName.startsWith('overview-my-transactions') ||
      pageName.startsWith('profile-my-transactions')
    ) {
      dispatch(
        applyTransactionColumnFilters({
          pageName,
          columnFilters: localColumnFilters,
        })
      );
    } else if (pageName === 'cards-payment-cards') {
      dispatch(
        applyCardsColumnFilters({
          pageName,
          columnFilters: localColumnFilters,
        })
      );
    } else if (pageName === 'team-members') {
      dispatch(
        applyUsersColumnFilters({
          pageName,
          columnFilters: localColumnFilters,
        })
      );
    } else if (pageName === 'team-card-groups') {
      dispatch(
        applyCardGroupsColumnFilters({
          pageName,
          columnFilters: localColumnFilters,
        })
      );
    } else if (pageName === 'team-project-groups') {
      dispatch(
        applyProjectGroupsColumnFilters({
          pageName,
          columnFilters: localColumnFilters,
        })
      );
    }

    closeSidesheet(SidesheetRoutes.TransactionsAdvancedSearch);
  };

  const handleClearColumnFilters = () => {
    // Clear URL parameters immediately
    clearFiltersFromCurrentUrl();

    // Create cleared filters that PRESERVE structure but reset values
    const currentFilters = tableFilters?.columnFilters || {};
    const clearedFilters = clearColumnFilterValues(currentFilters);

    // Update local state with cleared filters (structure preserved)
    setLocalColumnFilters(clearedFilters);

    // Clear Redux state
    if (
      pageName.startsWith('overview-transactions') ||
      pageName.startsWith('overview-my-transactions') ||
      pageName.startsWith('profile-my-transactions')
    ) {
      dispatch(clearTransactionColumnFilters({ pageName }));
    } else if (pageName === 'cards-payment-cards') {
      dispatch(applyCardsColumnFilters({ pageName, columnFilters: clearedFilters }));
    } else if (pageName === 'team-members') {
      dispatch(clearUsersColumnFilters({ pageName }));
    } else if (pageName === 'team-card-groups') {
      dispatch(clearCardGroupsColumnFilters({ pageName }));
    } else if (pageName === 'team-project-groups') {
      dispatch(clearProjectGroupsColumnFilters({ pageName }));
    }
  };

  // SEE-974: when a transaction type is active — either the action-bar quick filter
  // or the advanced filter's own (pending) Transaction type pick — only that type's
  // coding sections show; the others are hidden. No type → show everything.
  const visibleColumnFilters: ColumnFilter[] = useMemo(() => {
    const all = Object.values(localColumnFilters);
    // selectedValues may be FilterOption objects (with .id) or plain id strings
    // depending on the source — normalize to the id string either way.
    const codingTypeSel = (localColumnFilters.codingType?.selectedValues ?? []) as Array<
      string | { id?: string | null }
    >;
    const codingTypeIds = codingTypeSel.map((v) => (typeof v === 'string' ? v : v?.id ?? ''));
    const actionBarType = (tableFilters as { codingType?: string } | undefined)?.codingType;
    const actionBarTypeActive = ['gl', 'project', 'equipment'].includes((actionBarType ?? '').toLowerCase());
    const activeTypes = new Set(
      [actionBarType, ...codingTypeIds]
        .map((v) => (v ?? '').toLowerCase())
        .filter((v) => v === 'gl' || v === 'project' || v === 'equipment')
    );
    if (activeTypes.size === 0) return all;
    return all.filter((f) => {
      // The action-bar quick filter already controls the type, so its advanced
      // "Transaction type" section is redundant — hide it. (When the type is only
      // picked inside the advanced filter, keep it so it stays changeable.)
      if (f.key === 'codingType' && actionBarTypeActive) return false;
      const type = CODING_FILTER_KEY_TYPE[f.key];
      return !type || activeTypes.has(type);
    });
  }, [localColumnFilters, tableFilters]);

  return (
    <Box className="flex flex-col h-full px-12" sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {(() => {
          // For transaction pages, check filterOptionsLoading and filterOptions
          // For users page, check if columnFilters exist
          const isTransactionPage =
            pageName.startsWith('overview-transactions') ||
            pageName.startsWith('overview-my-transactions') ||
            pageName.startsWith('profile-my-transactions');

          if (isTransactionPage) {
            if (filterOptionsLoading || !tableFilters?.columnFilters) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                  }}
                >
                  {t('loadingFilters')}
                </Box>
              );
            }
            if (!filterOptions) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '300px',
                    gap: 2,
                    textAlign: 'center',
                    px: 4,
                  }}
                >
                  <Typography variant="h6" color={theme.palette.primary.main}>
                    {t('noFiltersAvailable')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('noFiltersAvailableDescription')}
                  </Typography>
                </Box>
              );
            }
          } else {
            // For users/cards pages, check if columnFilters exist
            if (!tableFilters?.columnFilters || Object.keys(tableFilters.columnFilters).length === 0) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '200px',
                  }}
                >
                  {t('loadingFilters')}
                </Box>
              );
            }
          }

          // Render filters (narrowed to the active transaction type, if any)
          return (
            localColumnFilters &&
            visibleColumnFilters.map((filter, i) => (
              <CriteriaColumnItem
                key={filter.key}
                filter={filter}
                isLast={i === visibleColumnFilters.length - 1}
                onFilterChange={handleSelectedValuesChange}
                onRuleChange={handleCriteriaRuleChange}
              />
            ))
          );
        })()}
      </Box>

      <Box className="flex self-end gap-16 py-8" sx={{ flexShrink: 0 }}>
        {/* <Button size="small">Save search</Button> */}
        <Button variant="outlined" size="small" onClick={handleClearColumnFilters}>
          {t('reset')}
        </Button>
        <Button
          data-testid="apply-filters-button"
          variant="contained"
          size="small"
          onClick={handleApplyColumnFilters}
        >
          {t('showResults')}
        </Button>
      </Box>
    </Box>
  );
}

export default TransactionAdvancedSearchSidesheet;
